/* eslint-disable */
import React from 'react';
import {
  TableHead, TableRow, TableCell, Checkbox, Box, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

/**
 * TableHeader
 *
 * Renders the <TableHead> with column labels.
 * Supports optional checkbox column and sortable columns.
 *
 * Props:
 *  columns        {array}    — column definitions
 *  selectable     {boolean}  — show leading checkbox column
 *  allSelected    {boolean}  — header checkbox state
 *  someSelected   {boolean}  — indeterminate state
 *  onSelectAll    {function} — header checkbox change handler
 *  sortField      {string}   — currently sorted column id
 *  sortDir        {string}   — 'asc' | 'desc'
 *  onSort         {function} — (columnId) => void
 *  hasGrouping    {boolean}  — if true, add extra indent cell
 */
const TableHeader = ({
  columns,
  selectable = false,
  allSelected = false,
  someSelected = false,
  onSelectAll,
  sortField,
  sortDir = 'asc',
  onSort,
  hasGrouping = false,
}) => {
  const handleSort = (col) => {
    if (!col.sortable || !onSort) return;
    onSort(col.id);
  };

  const SortIcon = ({ colId }) => {
    const isActive = colId === sortField;
    return isActive && sortDir === 'asc'
      ? <KeyboardArrowUpIcon sx={{ fontSize: 16, color: isActive ? '#385576' : '#C0C8D4' }} />
      : <KeyboardArrowDownIcon sx={{ fontSize: 16, color: isActive ? '#385576' : '#C0C8D4' }} />;
  };

  return (
    <TableHead sx={{ backgroundColor: '#fff' }}>
      <TableRow>
        {/* Grouping indent */}
        {hasGrouping && (
          <TableCell sx={{ width: 32, py: '14px', px: '8px', borderBottom: '1px solid #DEE5EE' }} />
        )}

        {/* Checkbox column */}
        {selectable && (
          <TableCell
            sx={{ width: 48, py: '14px', px: '16px', borderBottom: '1px solid #DEE5EE' }}
          >
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={onSelectAll}
              sx={{ color: '#DEE5EE', '&.Mui-checked': { color: '#007BC6' }, '&.MuiCheckbox-indeterminate': { color: '#007BC6' } }}
            />
          </TableCell>
        )}

        {/* Data columns */}
        {columns.map((col) => (
          <TableCell
            key={col.id}
            align={col.align || 'left'}
            onClick={() => handleSort(col)}
            sx={{
              py: '8px',
              px: '16px',
              fontWeight: 700,
              fontSize: '13px',
              color: '#385576',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              borderBottom: '1px solid #DEE5EE',
              width: col.width,
              whiteSpace: 'nowrap',
              cursor: col.sortable ? 'pointer' : 'default',
              userSelect: 'none',
              '&:hover': col.sortable ? { color: '#007BC6' } : {},
              ...col.headerSx,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {col.label}
              {col.sortable && <SortIcon colId={col.id} />}
            </Box>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export default TableHeader;
